import * as flatbuffers from "flatbuffers";
import { v4 as uuidv4 } from "uuid";
import {
  TraceEventBatch,
  TraceEventBatchT,
} from "../flatbuffers/generated/integration-trace/trace-event-batch";
import { TraceEventT } from "../flatbuffers/generated/integration-trace/trace-event";
import { TracePointId } from "../flatbuffers/generated/integration-trace/trace-point-id";
import { SourceComponent } from "../flatbuffers/generated/integration-trace/source-component";
import { TraceOutcome } from "../flatbuffers/generated/integration-trace/trace-outcome";
import { TracePath } from "../flatbuffers/generated/integration-trace/trace-path";
import { U128T } from "../flatbuffers/generated/u128/u128";

// ---------------------------------------------------------------------------
// Wire-format emit counter
// ---------------------------------------------------------------------------

declare global {
  interface Window {
    __INTEGRATION_TRACE_EMITS__?: Record<string, number>;
  }
}

/**
 * Key shape: `${format}|${tracePoint}|${"ok"|"err"}`.
 * Using a template-literal type to encode the three-part compound key.
 */
type CounterKey = `${string}|${number}|${"ok" | "err"}`;

const emitCounters = new Map<CounterKey, number>();

/**
 * Returns the cumulative emit count for the given (format, tracePoint, outcome) tuple.
 * Exported so unit tests can read the counter without dispatching DOM events.
 */
export function getEmitCount(
  format: string,
  tracePoint: TracePointId,
  outcome: "ok" | "err",
): number {
  const key: CounterKey = `${format}|${tracePoint}|${outcome}`;
  return emitCounters.get(key) ?? 0;
}

// Pending beacons accumulated within a tick — flushed in a single microtask
// to keep the hot decode path fast.  Each entry is the latest snapshot for
// that (format, tracePoint, outcome) tuple.
interface PendingBeacon {
  format: string;
  tracePoint: TracePointId;
  outcome: "ok" | "err";
  totalCount: number;
}
let pendingBeacons: Map<CounterKey, PendingBeacon> | null = null;

/**
 * Flush all pending CustomEvent beacons accumulated this tick.  Called
 * once per microtask after the first emit that lands in an empty batch.
 */
function flushBeacons(): void {
  if (pendingBeacons === null) {
    return;
  }
  const batch = pendingBeacons;
  pendingBeacons = null;
  if (typeof window === "undefined") {
    return;
  }
  Array.from(batch.values()).forEach((beacon) => {
    window.dispatchEvent(new CustomEvent("integration-trace-metric", { detail: beacon }));
  });
}

/**
 * Increment the counter for (format, tracePoint, outcome), persist to
 * `window.__INTEGRATION_TRACE_EMITS__`, and schedule a CustomEvent beacon
 * (coalesced via microtask to keep the hot path under ~1µs/emit).
 *
 * SSR-safe: the window accesses are guarded by `typeof window !== 'undefined'`.
 */
export function recordEmit(format: string, tracePoint: TracePointId, outcome: "ok" | "err"): void {
  const key: CounterKey = `${format}|${tracePoint}|${outcome}`;
  const totalCount = (emitCounters.get(key) ?? 0) + 1;
  emitCounters.set(key, totalCount);

  if (typeof window === "undefined") {
    return;
  }
  if (window.__INTEGRATION_TRACE_EMITS__ === undefined) {
    window.__INTEGRATION_TRACE_EMITS__ = {};
  }
  window.__INTEGRATION_TRACE_EMITS__[key] = totalCount;

  // Coalesce dispatch via microtask — one CustomEvent per (key) per tick.
  if (pendingBeacons === null) {
    pendingBeacons = new Map();
    queueMicrotask(flushBeacons);
  }
  pendingBeacons.set(key, { format, tracePoint, outcome, totalCount });
}

/**
 * Resets all emit counters and clears the window snapshot.
 * Intended for test teardown only — never call in production.
 */
export function resetEmitCounters(): void {
  emitCounters.clear();
  if (typeof window !== "undefined") {
    window.__INTEGRATION_TRACE_EMITS__ = {};
  }
}

// Maximum events to buffer before flushing early
const MAX_BATCH_SIZE = 64;
// Maximum milliseconds to hold events before flushing
const FLUSH_INTERVAL_MS = 50;
// WebSocket reconnect interval
const RECONNECT_INTERVAL_MS = 30_000;

/**
 * Input type for a single trace event. Only tracePoint is required;
 * correlationId is generated automatically when absent.
 */
export interface TraceEventInput {
  tracePoint: TracePointId;
  correlationId?: string; // UUID string, e.g. from uuidv4()
  outcome?: TraceOutcome;
  path?: TracePath;
  errorMessage?: string;
}

/**
 * Pack a UUID v4 string into U128T using big-endian byte order per wire_field_map.md §4.
 * UUID canonical bytes (RFC 4122) are interpreted as two BE u64 values: upper = bytes[0..8],
 * lower = bytes[8..16].
 */
export function uuidStringToU128T(uuidStr: string): U128T {
  const hex = uuidStr.replace(/-/g, "");
  const upper = BigInt(`0x${hex.slice(0, 16)}`);
  const lower = BigInt(`0x${hex.slice(16, 32)}`);
  return new U128T(upper, lower);
}

/**
 * Build a FBS TraceEventBatch body ready for WS transport.
 * Wire format: raw FBS body with "LNT2" file identifier, NO 4-byte length prefix
 * (WS frames are self-delimiting per wire_field_map.md §2).
 */
export function buildBatchBytes(events: TraceEventT[]): Uint8Array {
  const builder = new flatbuffers.Builder(4096);

  const batch = new TraceEventBatchT(
    SourceComponent.Frontend,
    // P0 placeholder test_run_id = U128 { upper: 0, lower: 0 } per wire_field_map.md §5
    new U128T(BigInt(0), BigInt(0)),
    events,
  );

  const offset = batch.pack(builder);
  // finishTraceEventBatchBuffer writes "LNT2" identifier, no size prefix
  TraceEventBatch.finishTraceEventBatchBuffer(builder, offset);
  return builder.asUint8Array();
}

/**
 * IntegrationTraceEmitter — buffers trace events and sends them as FBS
 * TraceEventBatch frames over WebSocket to the integrator's WS trace receiver.
 *
 * Best-effort: events are silently dropped when disconnected. The emitter
 * reconnects automatically every 30 seconds when the socket is closed.
 */
export class IntegrationTraceEmitter {
  private readonly url: string;

  private ws: WebSocket | null = null;

  private buffer: TraceEventT[] = [];

  private flushTimer: ReturnType<typeof setTimeout> | null = null;

  private reconnectTimer: ReturnType<typeof setTimeout> | null = null;

  private sequenceCounter: bigint = BigInt(0);

  private disposed = false;

  constructor(integratorWsUrl: string) {
    this.url = integratorWsUrl;
    this.connect();
  }

  private connect(): void {
    if (this.disposed) return;
    try {
      const ws = new WebSocket(this.url);
      ws.binaryType = "arraybuffer";
      ws.addEventListener("open", () => {
        // Connection established — cancel any pending reconnect
        if (this.reconnectTimer !== null) {
          clearTimeout(this.reconnectTimer);
          this.reconnectTimer = null;
        }
      });
      ws.addEventListener("close", () => {
        this.scheduleReconnect();
      });
      ws.addEventListener("error", () => {
        // error event is always followed by close; let the close handler reconnect
      });
      this.ws = ws;
    } catch {
      // WebSocket constructor can throw synchronously in some environments
      this.scheduleReconnect();
    }
  }

  private scheduleReconnect(): void {
    if (this.disposed) return;
    if (this.reconnectTimer !== null) return;
    this.reconnectTimer = setTimeout(() => {
      this.reconnectTimer = null;
      this.connect();
    }, RECONNECT_INTERVAL_MS);
  }

  /**
   * Queue a trace event. Flushes immediately when the buffer reaches MAX_BATCH_SIZE.
   * A 50ms deadline timer ensures events are sent promptly even under low load.
   */
  emit(input: TraceEventInput): void {
    if (this.disposed) return;

    const correlationId = input.correlationId ?? uuidv4();
    const tsNs = BigInt(Date.now()) * BigInt(1_000_000);
    this.sequenceCounter += BigInt(1);

    const event = new TraceEventT(
      uuidStringToU128T(correlationId),
      this.sequenceCounter,
      input.tracePoint,
      tsNs,
      input.outcome ?? TraceOutcome.Ok,
      input.path ?? TracePath.Forward,
      SourceComponent.Frontend,
    );

    if (input.errorMessage !== undefined) {
      event.errorMessage = input.errorMessage;
    }

    this.buffer.push(event);

    if (this.buffer.length >= MAX_BATCH_SIZE) {
      this.flush();
    } else if (this.flushTimer === null) {
      this.flushTimer = setTimeout(() => {
        this.flushTimer = null;
        this.flush();
      }, FLUSH_INTERVAL_MS);
    }
  }

  /**
   * Flush buffered events to the WebSocket immediately.
   * Silently discards when the socket is not open.
   */
  flush(): void {
    if (this.flushTimer !== null) {
      clearTimeout(this.flushTimer);
      this.flushTimer = null;
    }

    if (this.buffer.length === 0) return;
    // WebSocket.OPEN === 1; avoid globalThis.WebSocket.OPEN which may be
    // undefined in non-browser environments (e.g. Jest/jsdom).
    const WS_OPEN = 1;
    if (this.ws === null || this.ws.readyState !== WS_OPEN) {
      // Best-effort: drop on disconnect
      this.buffer = [];
      return;
    }

    const events = this.buffer.splice(0);
    try {
      const bytes = buildBatchBytes(events);
      // WS transport: raw FBS body, no 4-byte length prefix (wire_field_map.md §2)
      this.ws.send(new Uint8Array(bytes));
    } catch {
      // Buffer full or socket transitioning — best-effort, discard
    }
  }

  /**
   * Flush remaining events and close the WebSocket. Prevents further emission.
   */
  disconnect(): void {
    this.disposed = true;
    if (this.flushTimer !== null) {
      clearTimeout(this.flushTimer);
      this.flushTimer = null;
    }
    if (this.reconnectTimer !== null) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }
    this.flush();
    this.ws?.close();
    this.ws = null;
  }
}
